import React from 'react';
import ReactApexChart from 'react-apexcharts';
import jsonData from './sample.json';
import { formatElements } from './parseText';
import styled from 'styled-components';
import DemoReasoningApp from './reasoning';
import CustomHeatmap from './heatmap';
import Scatter from './scatterChart'
import roiData from './roiData.json'

const Container = styled.div`
  padding: 20px;
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Content = styled.div`
  max-width: 700px;
`;


/**
 * ChartComponent
 * Example bar chart component for <Chart chart_id="..."> placeholders
 */
const ChartComponent = ({ chartInfo }) => {
  if (!chartInfo || !chartInfo.factors || chartInfo.factors.length === 0) {
    return <div>No data available for this chart.</div>;
  }

  // We'll assume the first factor is the reference for sorting
  const firstFactor = chartInfo.factors[0];

  /**
   * 1. Rebase from 0–10 to 0–100 by multiplying each score by 10.
   * 2. Round down via Math.floor.
   * 3. Sort (descending) based on the first factor's data.
   */
  const sortedFirstFactorData = [...firstFactor.data]
    .map((item) => ({
      ...item,
      // Rebase from 10 → 100, then round down.
      score: Math.floor(item.score * 10),
    }))
    .sort((a, b) => b.score - a.score);

  // Build categories from the sorted data
  const categories = sortedFirstFactorData.map((item) => item.name);

  // For each factor, reorder its data according to the new sorted order
  const series = chartInfo.factors.map((factor) => {
    // Build a lookup by name
    const factorLookup = factor.data.reduce((acc, item) => {
      // Rebase each score from 10 to 100, then round down
      acc[item.name] = Math.floor(item.score * 10);
      return acc;
    }, {});

    // Rebuild the data array in sorted order
    const reorderedScores = sortedFirstFactorData.map(
      (item) => factorLookup[item.name] ?? 0
    );

    return {
      name: factor.factor,
      data: reorderedScores,
    };
  });

  /*
    ApexCharts configuration
    - Single color for all bars
    - Removes dataLabels to hide numeric labels on the bars
    - y-axis from 0–100
  */
  const options = {
    chart: {
      height: 400,
      type: 'bar',
      id: 'my-bar-chart',
      fontFamily: 'Raleway, sans-serif',
      foreColor: '#7E7E7E',
      toolbar: { show: false },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350,
        },
      },
    },
    colors: ['#2D70E2'],
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 5,
        barHeight: '70%',
      },
    },
    legend: {
      show: false,
    },
    fill: {
      type: 'solid',
    },
    grid: {
      xaxis: {
        lines: { show: true },
      },
      yaxis: {
        lines: { show: false },
      },
    },
    xaxis: {
      categories,
      title: {
        text: firstFactor.xAxisTitle || 'Categories',
      },
      labels: {
        style: {
          fontFamily: 'Raleway, sans-serif',
        },
      },
    },
    yaxis: {
      min: 0,
      max: 100, // Because we're now scaled 0–100
      title: {
        text: firstFactor.yAxisTitle || 'Score',
      },
      labels: {
        style: {
          fontFamily: 'Raleway, sans-serif',
        },
      },
    },
    dataLabels: {
      enabled: false, // Don't display values on each bar
    },
  };

  return (
    <div style={{ margin: '1rem 0', width: '100%' }}>
      <ReactApexChart options={options} series={series} type="bar" height={400} />
    </div>
  );
};

/**
 * HeatmapComponent
 * Renders an ApexCharts heatmap given a "simple heatmap" data structure.
 * Expects chartInfo with:
 *  {
 *    rowTitles: ['Factor A', 'Factor B', ...],
 *    columnTitles: ['Dept X', 'Dept Y', ...],
 *    scores: [
 *      [someNumber, ...], // Factor A scores
 *      [someNumber, ...], // Factor B scores
 *      ...
 *    ]
 *  }
 */
const HeatmapComponent = ({ chartInfo }) => {
  if (!chartInfo || !chartInfo.scores || chartInfo.scores.length === 0) {
    return <div>No data available for this heatmap.</div>;
  }

  const { rowTitles, columnTitles, scores } = chartInfo;

  /**
   * Rebase the scores: multiply each by 10 if originally 0–10 (adjust as needed).
   * Round them if needed. 
   *
   * ApexCharts for heatmaps expects data in the format:
   * series = [
   *   {
   *     name: rowTitle,
   *     data: [{ x: colTitle1, y: 10 }, { x: colTitle2, y: 20 }, ...]
   *   },
   *   ...
   * ];
   */
  const series = rowTitles.map((rowTitle, rowIdx) => {
    const rowData = scores[rowIdx] || [];
    const dataArray = rowData.map((val, colIdx) => ({
      x: columnTitles[colIdx],
      // multiply by 10, or any scale you want
      y: val != null ? Math.floor(val * 10) : 0,
    }));

    return {
      name: rowTitle,
      data: dataArray,
    };
  });

  const options = {
    chart: {
      height: 350,
      type: 'heatmap',
      id: 'my-heatmap',
      fontFamily: 'Raleway, sans-serif',
      foreColor: '#7E7E7E',
      toolbar: { show: false },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350,
        },
      },
    },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.6,
        colorScale: {
          ranges: [
            { from: 0, to: 30, name: 'Low', color: '#00A100' },
            { from: 31, to: 70, name: 'Medium', color: '#128FD9' },
            { from: 71, to: 100, name: 'High', color: '#FFB200' },
          ],
        },
      },
    },
    dataLabels: {
      enabled: false, // Hide numeric labels on each cell
    },
    xaxis: {
      labels: {
        rotate: -45,
        style: {
          fontFamily: 'Raleway, sans-serif',
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          fontFamily: 'Raleway, sans-serif',
        },
      },
    },
    legend: {
      position: 'bottom',
      markers: { shape: 'circle' },
    },
    tooltip: {
      theme: 'dark',
    },
  };

  return (
    <div style={{ margin: '1rem 0', width: '100%' }}>
      <ReactApexChart options={options} series={series} type="heatmap" height={350} />
    </div>
  );
};

const MyReport = () => {
  // Extract 'report' text and 'chart_data' from the JSON
  const { report, chart_data } = jsonData;

  /**
   * parseContent
   * Splits the report text on either <Chart chart_id="x"> or <Heatmap chart_id="y">,
   * and replaces them with the appropriate component.
   */
  const parseContent = () => {
    const output = [];
    let lastIndex = 0;

    // Regex to find <Chart chart_id="(\d+)"> or <Heatmap chart_id="(\d+)">
    // We capture the type in group 1 and the ID in group 2
    const regex = /<(Chart|Heatmap)\s+chart_id\s*=\s*"?(\d+)"?\s*>/g;
    let match;

    while ((match = regex.exec(report)) !== null) {
      const matchIndex = match.index;
      const textChunk = report.substring(lastIndex, matchIndex);

      // Insert any text prior to the placeholder
      if (textChunk) {
        output.push(formatElements(textChunk));
      }

      // Extract the type ("Chart" or "Heatmap") and the numeric chart ID
      const componentType = match[1];
      const chartId = parseInt(match[2], 10);

      // Insert the appropriate React component
      if (componentType === 'Chart') {
        output.push(
          <ChartComponent
            key={`chart-${chartId}`}
            chartInfo={chart_data[chartId]}
          />
        );
      } else if (componentType === 'Heatmap') {
        output.push(
          <CustomHeatmap data={chart_data[chartId]} />
        );
      }

      lastIndex = regex.lastIndex;
    }

    // Add any remaining text after the last placeholder
    if (lastIndex < report.length) {
      const remainingText = report.substring(lastIndex);
      output.push(formatElements(remainingText));
    }

    return output;
  };

  return (
    <Container>
      <Scatter data={roiData} />
      <Content>{parseContent()}</Content>
    </Container>
  );
};

export default MyReport;
